import { NavLink, Outlet } from "react-router";
import { ACCESS, ROLE_AR, useAuth } from "../lib/auth.js";
import { useConflictCount } from "../lib/queries.js";
import type { Role } from "../lib/types.js";
import { Button, cx } from "./kit.js";

interface NavItem {
  to: string;
  label: string;
  roles: Role[];
  end?: boolean;
}

/**
 * Nav order is the manager's day, not the data model's shape: what is happening now, then the
 * thing that needs a human, then the money he can't see today.
 */
const NAV: Array<{ heading: string; items: NavItem[] }> = [
  {
    heading: "اليوم",
    items: [
      { to: "/", label: "نظرة عامة", roles: ACCESS.office, end: true },
      { to: "/board", label: "لوحة التوزيع", roles: ACCESS.office },
      { to: "/conflicts", label: "التعارضات", roles: ACCESS.conflicts },
    ],
  },
  {
    heading: "العمل",
    items: [
      { to: "/jobs", label: "المهام", roles: ACCESS.office },
      { to: "/customers", label: "الزبائن", roles: ACCESS.office },
      { to: "/performance", label: "أداء الفنيين", roles: ACCESS.office },
    ],
  },
  {
    heading: "المال",
    items: [
      { to: "/invoices", label: "الفواتير", roles: ACCESS.money },
      { to: "/payments", label: "المقبوضات", roles: ACCESS.money },
      { to: "/accounting", label: "المحاسبة", roles: ACCESS.money },
    ],
  },
  {
    heading: "الإعداد",
    items: [
      { to: "/materials", label: "المواد والأسعار", roles: ACCESS.office },
      { to: "/settings", label: "الإعدادات", roles: ACCESS.users },
    ],
  },
];

function ConflictPip() {
  const { data } = useConflictCount();
  const n = data?.unresolved ?? 0;
  if (n === 0) return null;
  return (
    <span className="num mr-auto rounded bg-bad px-1.5 py-0.5 text-[11px] font-bold text-white">
      {n}
    </span>
  );
}

export function Shell() {
  const { user, logout, can } = useAuth();
  const showConflicts = can(...ACCESS.conflicts);

  return (
    <div className="flex min-h-full">
      <aside className="sticky top-0 flex h-screen w-56 shrink-0 flex-col border-l border-line bg-surface">
        <div className="border-b border-line px-4 py-4">
          <p className="text-lg font-bold text-ink">فني</p>
          <p className="text-xs text-ink-faint">لوحة الإدارة</p>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {NAV.map((group) => {
            const items = group.items.filter((i) => can(...i.roles));
            if (items.length === 0) return null;
            return (
              <div key={group.heading} className="mb-4">
                <p className="px-2 pb-1 text-[11px] font-semibold text-ink-faint">{group.heading}</p>
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cx(
                        "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition",
                        isActive
                          ? "bg-accent-soft font-semibold text-accent"
                          : "text-ink-soft hover:bg-line-soft",
                      )
                    }
                  >
                    {item.label}
                    {item.to === "/conflicts" && showConflicts ? <ConflictPip /> : null}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-line px-4 py-3">
          <p className="truncate text-sm font-medium text-ink">{user?.name}</p>
          <p className="text-xs text-ink-faint">{user ? ROLE_AR[user.role] : ""}</p>
          <Button variant="ghost" size="sm" className="mt-2 w-full" onClick={logout}>
            تسجيل الخروج
          </Button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 px-6 py-6">
        <Outlet />
      </main>
    </div>
  );
}
